import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Plus, X, Trash2, Edit2, AlertCircle, Search, Filter } from 'lucide-react';
import './ResourceManagement.css';

const ResourceManagement = () => {
  const { authState } = useOktaAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [showEditResourceModal, setShowEditResourceModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('All Environments');
  const [selectedRisk, setSelectedRisk] = useState('All Risk Levels');
  const [resourceFormData, setResourceFormData] = useState({
    targetSystem: '',
    accessEnvironment: '',
    accessResource: '',
    dataRisk: '',
    resourceTeamManager: ''
  });

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/resources');
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const handleAddResource = async (e) => {
    e.preventDefault();
    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/resources', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...resourceFormData,
          createdBy: currentUser
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setResources(prev => [...prev, result.resource]);
        setResourceFormData({
          targetSystem: '',
          accessEnvironment: '',
          accessResource: '',
          dataRisk: '',
          resourceTeamManager: ''
        });
        setShowAddResourceModal(false);
        
        // Show success notification
        showNotification(`Resource "${resourceFormData.accessResource}" created successfully!`, 'success');
      } else {
        const error = await response.json();
        alert('Error adding resource: ' + error.error);
      }
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Error adding resource. Please try again.');
    }
  };

  const handleEditResource = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/resources/${editingResource.ResourceID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resourceFormData),
      });

      if (response.ok) {
        const result = await response.json();
        setResources(prev => prev.map(resource => 
          resource.ResourceID === editingResource.ResourceID ? result.resource : resource
        ));
        setShowEditResourceModal(false);
        setEditingResource(null);
        showNotification(`Resource "${resourceFormData.accessResource}" updated successfully!`, 'success');
      } else {
        const error = await response.json();
        alert('Error updating resource: ' + error.error);
      }
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('Error updating resource. Please try again.');
    }
  };

  const handleDeleteResource = (resourceId) => {
    const resource = resources.find(r => r.ResourceID === resourceId);
    setResourceToDelete(resource);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteResource = async () => {
    if (!resourceToDelete) return;

    try {
      const response = await fetch(`/api/resources/${resourceToDelete.ResourceID}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setResources(prev => prev.filter(resource => resource.ResourceID !== resourceToDelete.ResourceID));
        showNotification(`Resource "${resourceToDelete.AccessResource}" has been deleted`, 'delete');
      } else {
        alert('Error deleting resource. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Error deleting resource. Please check your connection and try again.');
    } finally {
      setShowDeleteConfirmModal(false);
      setResourceToDelete(null);
    }
  };

  const openEditModal = (resource) => {
    setEditingResource(resource);
    setResourceFormData({
      targetSystem: resource.TargetSystem || '',
      accessEnvironment: resource.AccessEnvironment || '',
      accessResource: resource.AccessResource || '',
      dataRisk: resource.DataRisk || '',
      resourceTeamManager: resource.ResourceTeamManager || ''
    });
    setShowEditResourceModal(true);
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `success-notification ${type === 'delete' ? 'delete-notification' : ''}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${type === 'delete' ? '🗑️' : '✓'}</span>
        <span class="notification-message">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  if (loading) {
    return (
      <div className="resource-management">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading resources data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="resource-management">
      <div className="resource-header">
        <div>
          <h2>System Resources</h2>
          <p>Manage available systems and access permissions</p>
        </div>
        <div className="header-actions">
          <button 
            className="add-resource-btn"
            onClick={() => setShowAddResourceModal(true)}
          >
            <Plus size={16} />
            Add Resource
          </button>
        </div>
      </div>

      {/* Add Resource Modal */}
      {showAddResourceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Resource</h3>
              <button className="close-btn" onClick={() => setShowAddResourceModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddResource} className="modal-form">
              <div className="form-group">
                <label htmlFor="targetSystem">Target System or Product *</label>
                <input
                  type="text"
                  id="targetSystem"
                  value={resourceFormData.targetSystem}
                  onChange={e => setResourceFormData(prev => ({ ...prev, targetSystem: e.target.value }))}
                  placeholder="e.g., SAP, Salesforce, AWS"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="accessEnvironment">Access Environment *</label>
                <select
                  id="accessEnvironment"
                  value={resourceFormData.accessEnvironment}
                  onChange={e => setResourceFormData(prev => ({ ...prev, accessEnvironment: e.target.value }))}
                  required
                >
                  <option value="">Select Environment</option>
                  <option value="Dev">Development</option>
                  <option value="QA">Quality Assurance</option>
                  <option value="Prod">Production</option>
                  <option value="Staging">Staging</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="accessResource">Access Resource *</label>
                <input
                  type="text"
                  id="accessResource"
                  value={resourceFormData.accessResource}
                  onChange={e => setResourceFormData(prev => ({ ...prev, accessResource: e.target.value }))}
                  placeholder="e.g., Database Access, API Gateway, File System"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="dataRisk">Data Risk *</label>
                <select
                  id="dataRisk"
                  value={resourceFormData.dataRisk}
                  onChange={e => setResourceFormData(prev => ({ ...prev, dataRisk: e.target.value }))}
                  required
                >
                  <option value="">Select Risk Level</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="resourceTeamManager">Resource Team Manager *</label>
                <input
                  type="text"
                  id="resourceTeamManager"
                  value={resourceFormData.resourceTeamManager}
                  onChange={e => setResourceFormData(prev => ({ ...prev, resourceTeamManager: e.target.value }))}
                  placeholder="Manager responsible for this resource"
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddResourceModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Add Resource
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Resource Modal */}
      {showEditResourceModal && editingResource && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Resource</h3>
              <button className="close-btn" onClick={() => setShowEditResourceModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditResource} className="modal-form">
              <div className="form-group">
                <label htmlFor="editTargetSystem">Target System or Product *</label>
                <input
                  type="text"
                  id="editTargetSystem"
                  value={resourceFormData.targetSystem}
                  onChange={e => setResourceFormData(prev => ({ ...prev, targetSystem: e.target.value }))}
                  placeholder="e.g., SAP, Salesforce, AWS"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editAccessEnvironment">Access Environment *</label>
                <select
                  id="editAccessEnvironment"
                  value={resourceFormData.accessEnvironment}
                  onChange={e => setResourceFormData(prev => ({ ...prev, accessEnvironment: e.target.value }))}
                  required
                >
                  <option value="">Select Environment</option>
                  <option value="Dev">Development</option>
                  <option value="QA">Quality Assurance</option>
                  <option value="Prod">Production</option>
                  <option value="Staging">Staging</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editAccessResource">Access Resource *</label>
                <input
                  type="text"
                  id="editAccessResource"
                  value={resourceFormData.accessResource}
                  onChange={e => setResourceFormData(prev => ({ ...prev, accessResource: e.target.value }))}
                  placeholder="e.g., Database Access, API Gateway, File System"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editDataRisk">Data Risk *</label>
                <select
                  id="editDataRisk"
                  value={resourceFormData.dataRisk}
                  onChange={e => setResourceFormData(prev => ({ ...prev, dataRisk: e.target.value }))}
                  required
                >
                  <option value="">Select Risk Level</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editResourceTeamManager">Resource Team Manager *</label>
                <input
                  type="text"
                  id="editResourceTeamManager"
                  value={resourceFormData.resourceTeamManager}
                  onChange={e => setResourceFormData(prev => ({ ...prev, resourceTeamManager: e.target.value }))}
                  placeholder="Manager responsible for this resource"
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditResourceModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Update Resource
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && resourceToDelete && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <div className="delete-icon-header">
                <div className="delete-icon-circle">
                  <Trash2 size={24} />
                </div>
                <h3>Delete Resource</h3>
              </div>
              <button className="close-btn" onClick={() => setShowDeleteConfirmModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-form">
              <div className="delete-content">
                <p className="delete-resource-name">
                  Are you sure you want to delete <strong>"{resourceToDelete.AccessResource}"</strong>?
                </p>
                <div className="delete-warning">
                  <AlertCircle size={16} />
                  <span>This action will permanently delete the resource. This cannot be undone.</span>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowDeleteConfirmModal(false)} 
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={confirmDeleteResource} 
                  className="btn-delete"
                >
                  <Trash2 size={16} />
                  Delete Resource
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="rbac-filters">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search by resource name, system, manager..."
            className="search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <div className="filter-group">
            <Filter className="filter-icon" />
            <select
              className="filter-select"
              value={selectedEnvironment}
              onChange={e => setSelectedEnvironment(e.target.value)}
            >
              <option>All Environments</option>
              {[...new Set(resources.map(r => r.AccessEnvironment))].filter(Boolean).sort().map(env => (
                <option key={env} value={env}>{env}</option>
              ))}
            </select>
          </div>
          <select
            className="filter-select"
            value={selectedRisk}
            onChange={e => setSelectedRisk(e.target.value)}
          >
            <option>All Risk Levels</option>
            {['Low', 'Medium', 'High', 'Critical'].map(risk => (
              <option key={risk} value={risk}>{risk}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Resources Table */}
      <div className="resources-table-container">
        <div className="table-header">
          <h3>Resources ({(() => {
            const filtered = resources.filter(resource => {
              const matchesSearch = !searchQuery || 
                resource.AccessResource.toLowerCase().includes(searchQuery.toLowerCase()) ||
                resource.TargetSystem.toLowerCase().includes(searchQuery.toLowerCase()) ||
                resource.ResourceTeamManager.toLowerCase().includes(searchQuery.toLowerCase()) ||
                resource.AccessEnvironment.toLowerCase().includes(searchQuery.toLowerCase());
              
              const matchesEnvironment = selectedEnvironment === 'All Environments' || 
                resource.AccessEnvironment === selectedEnvironment;
              
              const matchesRisk = selectedRisk === 'All Risk Levels' || 
                resource.DataRisk === selectedRisk;
              
              return matchesSearch && matchesEnvironment && matchesRisk;
            });
            return filtered.length;
          })()} of {resources.length})</h3>
          {(searchQuery || selectedEnvironment !== 'All Environments' || selectedRisk !== 'All Risk Levels') && (
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchQuery('');
                setSelectedEnvironment('All Environments');
                setSelectedRisk('All Risk Levels');
              }}
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>
        
        {resources.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>No resources found</h3>
            <p>Click "Add Resource" to create your first resource.</p>
          </div>
        ) : (
          <div className="resources-table-wrapper">
            <table className="resources-table">
              <thead>
                <tr>
                  <th>Target System or Product</th>
                  <th>Access Environment</th>
                  <th>Access Resource</th>
                  <th>Data Risk</th>
                  <th>Resource Team Manager</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {resources.filter(resource => {
                  const matchesSearch = !searchQuery || 
                    resource.AccessResource.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.TargetSystem.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.ResourceTeamManager.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.AccessEnvironment.toLowerCase().includes(searchQuery.toLowerCase());
                  
                  const matchesEnvironment = selectedEnvironment === 'All Environments' || 
                    resource.AccessEnvironment === selectedEnvironment;
                  
                  const matchesRisk = selectedRisk === 'All Risk Levels' || 
                    resource.DataRisk === selectedRisk;
                  
                  return matchesSearch && matchesEnvironment && matchesRisk;
                }).length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="empty-state">
                        <AlertCircle size={48} className="empty-icon" />
                        <h3>No resources match your filters</h3>
                        <p>Try adjusting your search or filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                ) : resources.filter(resource => {
                  const matchesSearch = !searchQuery || 
                    resource.AccessResource.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.TargetSystem.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.ResourceTeamManager.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    resource.AccessEnvironment.toLowerCase().includes(searchQuery.toLowerCase());
                  
                  const matchesEnvironment = selectedEnvironment === 'All Environments' || 
                    resource.AccessEnvironment === selectedEnvironment;
                  
                  const matchesRisk = selectedRisk === 'All Risk Levels' || 
                    resource.DataRisk === selectedRisk;
                  
                  return matchesSearch && matchesEnvironment && matchesRisk;
                }).map(resource => (
                  <tr key={resource.ResourceID}>
                    <td className="system-name">{resource.TargetSystem}</td>
                    <td>
                      <span className={`environment-badge environment-${resource.AccessEnvironment?.toLowerCase()}`}>
                        {resource.AccessEnvironment}
                      </span>
                    </td>
                    <td className="resource-name">{resource.AccessResource}</td>
                    <td>
                      <span className={`risk-badge risk-${resource.DataRisk?.toLowerCase()}`}>
                        {resource.DataRisk}
                      </span>
                    </td>
                    <td>{resource.ResourceTeamManager}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn" 
                          onClick={() => openEditModal(resource)}
                          title="Edit resource"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          className="delete-btn" 
                          onClick={() => handleDeleteResource(resource.ResourceID)}
                          title="Delete resource"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceManagement;
